import type { CallOff } from '../types';

import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysCallOff } from '../queryKeys';

import { fetchCallOff } from './fetchCallOff';

/**
 * Generates the query options for the call-off query.
 *
 * @param callOffId - The ID of the call-off to fetch.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getCallOffQueryOptions = <TData = CallOff>({
  callOffId,
  options,
}: {
  callOffId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<CallOff, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: callOffId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchCallOff(callOffId, { signal })
      : skipToken,
    queryKey: queryKeysCallOff.get(callOffId ?? ''),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query based custom hook for getting the data for a single call-off by ID.
 * Returns the call-off with all line items.
 *
 * @param callOffId - The ID of the call-off to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Off/operation/get_calloff_calloffs__calloff_id__get
 */
export const useQueryCallOff = (
  callOffId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<CallOff, Error, CallOff>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getCallOffQueryOptions<CallOff>({ callOffId, options }));
